import { CalendarCheck, CalendarPlus } from "lucide-react";
import { PaginationBar } from "../../../../components/PaginationBar";
import type { ScheduleSearchItem, ScheduleSearchMeta } from "../../../../services/scheduleCard";
import {
  FLOW_PRIMARY_BUTTON_BASE,
  FLOW_SECONDARY_BUTTON_BASE,
} from "../../../../styles/twDesignPrimitives";
import { cn } from "../../../../utils/cn";
import {
  formatPersonName,
  formatPropertyAddress,
  formatPropertyLabel,
  formatScheduleCardStatus,
  formatScheduleDateTime,
} from "../../../../utils/scheduleSearchDisplay";

type Props = {
  items: ScheduleSearchItem[];
  meta: ScheduleSearchMeta;
  isSearching: boolean;
  hasSearched: boolean;
  pendingScheduleId: number | null;
  creatingScheduleId: number | null;
  onAskCreate: (scheduleId: number) => void;
  onCancelCreate: () => void;
  onConfirmCreate: (scheduleId: number) => void;
  onPageChange: (page: number) => void;
};

export function ScheduleSearchResults({
  items,
  meta,
  isSearching,
  hasSearched,
  pendingScheduleId,
  creatingScheduleId,
  onAskCreate,
  onCancelCreate,
  onConfirmCreate,
  onPageChange,
}: Props) {
  if (isSearching && items.length === 0) {
    return <p className="py-8 text-center text-sm text-slate-500">Buscando agendamentos...</p>;
  }

  if (hasSearched && items.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-slate-500">
        Nenhum agendamento encontrado
      </p>
    );
  }

  if (items.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-slate-400">
        Busque por cliente ou por data para listar agendamentos.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <ul className="flex max-h-[42vh] flex-col gap-2 overflow-y-auto pr-1">
        {items.map((item) => {
          const isPending = pendingScheduleId === item.id;
          const isCreating = creatingScheduleId === item.id;

          return (
            <li
              key={item.id}
              className="rounded-xl border border-slate-200 bg-white p-3 text-left shadow-sm"
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-800">
                    {formatPersonName(item.client, "Cliente não informado")}
                  </p>
                  <p className="mt-1 text-xs font-medium text-slate-600">
                    {formatScheduleDateTime(item.date)}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    {formatPropertyLabel(item.property)}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    Consultor: {formatPersonName(item.consultant, "Não informado")}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-400">
                    {formatPropertyAddress(item.property?.address)}
                  </p>
                  <p className={`mt-1 text-xs font-medium ${item.has_card ? "text-slate-500" : "text-orange-600"}`}>
                    {formatScheduleCardStatus(item)}
                  </p>
                </div>

                {item.can_create_card ? (
                  isPending ? (
                    <div className="flex shrink-0 flex-col gap-2 sm:items-end">
                      <button
                        type="button"
                        disabled={isCreating}
                        onClick={() => onConfirmCreate(item.id)}
                        className={cn(FLOW_PRIMARY_BUTTON_BASE, "h-9 px-3 text-xs")}
                      >
                        {isCreating ? "Criando..." : "Confirmar criação"}
                      </button>
                      <button
                        type="button"
                        disabled={isCreating}
                        onClick={onCancelCreate}
                        className={cn(FLOW_SECONDARY_BUTTON_BASE, "h-9 px-3 text-xs")}
                      >
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onAskCreate(item.id)}
                      className={cn(FLOW_PRIMARY_BUTTON_BASE, "h-9 shrink-0 px-3 text-xs")}
                    >
                      <CalendarPlus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                      Criar card
                    </button>
                  )
                ) : (
                  <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-500">
                    <CalendarCheck className="h-3.5 w-3.5" aria-hidden />
                    Card já criado
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {meta.last_page > 1 && (
        <PaginationBar
          currentPage={meta.current_page}
          totalPages={meta.last_page}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}
